import { type ComponentType, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type UserSubOrderV2 } from '@redteclab/api/clients/bully'
import { IconOpenInLaunchExternal } from '@redteclab/icons'

import { useGlobalConfigContext } from '../../../global-config'
import {
  subOrderStatusAccessorGetAfterSalesSelfServiceAvailability,
  subOrderStatusAccessorGetDocumentsSelfServiceAvailability,
  subOrderStatusAccessorGetOrderModificationSelfServiceAvailability,
  subOrderStatusAccessorGetReturnLabelSelfServiceAvailability,
  subOrderStatusAccessorGetTrackingSelfServiceAvailability,
} from '../../../order'
import {
  parcelLabAccessorGetParcelLabDeliveredStatus,
  useApiParcelLabSwrGetOrderStatus,
} from '../../../parcel-lab'

import { useContactPageLastOrderContext } from './ContactPageLastOrderContext'
import { ContactPageSelfServiceShortcutCancelOrderCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutCancelOrderCta'
import { ContactPageSelfServiceShortcutChangeOrderCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutChangeOrderCta'
import { ContactPageSelfServiceShortcutDocumentsAndInvoices } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutDocumentsAndInvoices'
import { ContactPageSelfServiceShortcutOtherTopicsCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutOtherTopicsCta'
import { ContactPageSelfServiceShortcutPaymentInformationCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutPaymentInformationCta'
import {
  type ContactPageSelfServiceShortcutAvailability,
  type ContactPageSelfServiceShortcutProps,
} from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutProps'
import { ContactPageSelfServiceShortcutReportDamagedOrderCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutReportDamagedOrderCta'
import { ContactPageSelfServiceShortcutReturnCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutReturnCta'
import { ContactPageSelfServiceShortcutReturnLabelCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutReturnLabelCta'
import { ContactPageSelfServiceShortcutSubOrderTrackingCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutSubOrderTrackingCta'
import { ContactPageSelfServiceShortcutViewAllCta } from './ContactPageSelfServiceShortcuts/ContactPageSelfServiceShortcutViewAllCta'

const MAX_VISIBLE_SHORTCUT_CTAS = 6
const MIN_SHORTCUT_CTAS_FOR_VIEW_ALL = 5

type ShortcutCta = {
  Cta: ComponentType<ContactPageSelfServiceShortcutProps>
  getAvailability: (
    subOrder: UserSubOrderV2,
    isParcelLabDelivered: boolean,
  ) => ContactPageSelfServiceShortcutAvailability
  key: string
}

type ShortcutCtaWithAvailability = ShortcutCta & {
  availability: ContactPageSelfServiceShortcutAvailability
}

const viewAllShortcutCta: ShortcutCtaWithAvailability = {
  availability: 'enabled',
  Cta: ContactPageSelfServiceShortcutViewAllCta,
  getAvailability: () => 'enabled',
  key: 'view-all',
}

const primaryShortcutCtas: ShortcutCta[] = [
  {
    Cta: ContactPageSelfServiceShortcutReturnLabelCta,
    getAvailability:
      subOrderStatusAccessorGetReturnLabelSelfServiceAvailability,
    key: 'return-label',
  },
  {
    Cta: ContactPageSelfServiceShortcutSubOrderTrackingCta,
    getAvailability: subOrderStatusAccessorGetTrackingSelfServiceAvailability,
    key: 'tracking',
  },
  {
    Cta: ContactPageSelfServiceShortcutCancelOrderCta,
    getAvailability:
      subOrderStatusAccessorGetOrderModificationSelfServiceAvailability,
    key: 'cancel-order',
  },
  {
    Cta: ContactPageSelfServiceShortcutReturnCta,
    getAvailability: subOrderStatusAccessorGetAfterSalesSelfServiceAvailability,
    key: 'return-order',
  },
  {
    Cta: ContactPageSelfServiceShortcutReportDamagedOrderCta,
    getAvailability: subOrderStatusAccessorGetAfterSalesSelfServiceAvailability,
    key: 'report-damaged-order',
  },
  {
    Cta: ContactPageSelfServiceShortcutChangeOrderCta,
    getAvailability:
      subOrderStatusAccessorGetOrderModificationSelfServiceAvailability,
    key: 'change-order',
  },
  {
    Cta: ContactPageSelfServiceShortcutDocumentsAndInvoices,
    getAvailability: subOrderStatusAccessorGetDocumentsSelfServiceAvailability,
    key: 'documents-and-invoices',
  },
  {
    Cta: ContactPageSelfServiceShortcutPaymentInformationCta,
    getAvailability: () => 'enabled',
    key: 'payment-information',
  },
  {
    Cta: ContactPageSelfServiceShortcutOtherTopicsCta,
    getAvailability: () => 'enabled',
    key: 'other-topics',
  },
]

const getVisibleShortcutCtas = (
  availablePrimaryShortcutCtas: ShortcutCtaWithAvailability[],
): ShortcutCtaWithAvailability[] => {
  const showViewAllShortcutCta =
    availablePrimaryShortcutCtas.length >= MIN_SHORTCUT_CTAS_FOR_VIEW_ALL

  const visiblePrimaryShortcutCtaCount = showViewAllShortcutCta
    ? MAX_VISIBLE_SHORTCUT_CTAS - 1
    : MAX_VISIBLE_SHORTCUT_CTAS

  const disabledPrimaryShortcutCtas = availablePrimaryShortcutCtas.filter(
    ({ availability }) => availability === 'disabled',
  )

  const enabledPrimaryShortcutCtas = availablePrimaryShortcutCtas.filter(
    ({ availability }) => availability === 'enabled',
  )

  const visibleEnabledShortcutCtaCount = Math.max(
    visiblePrimaryShortcutCtaCount - disabledPrimaryShortcutCtas.length,
    0,
  )

  const visiblePrimaryShortcutCtas = [
    ...enabledPrimaryShortcutCtas.slice(0, visibleEnabledShortcutCtaCount),
    ...disabledPrimaryShortcutCtas,
  ]

  return showViewAllShortcutCta
    ? [...visiblePrimaryShortcutCtas, viewAllShortcutCta]
    : visiblePrimaryShortcutCtas
}

export const ContactPageLastOrderShortcutsCtaList: FC = () => {
  const { language } = useGlobalConfigContext()
  const { activeSubOrder, firstSubOrder, lastOrder } =
    useContactPageLastOrderContext()
  const subOrder = activeSubOrder ?? firstSubOrder
  const orderNumber = lastOrder?.orderMasterData?.orderNumber
  const { data: parcelLabData } = useApiParcelLabSwrGetOrderStatus(
    language,
    subOrder?.carrier?.trackingId,
    subOrder?.carrier?.externalCarrierIds?.parcellab,
  )

  if (!orderNumber || !subOrder?.subOrderId) {
    return null
  }

  const isParcelLabDelivered = parcelLabAccessorGetParcelLabDeliveredStatus(
    subOrder,
    parcelLabData,
  )

  const availablePrimaryShortcutCtas = primaryShortcutCtas
    .map((shortcutCta) => ({
      ...shortcutCta,
      availability: shortcutCta.getAvailability(subOrder, isParcelLabDelivered),
    }))
    .filter(({ availability }) => availability !== 'hidden')

  const visibleShortcutCtas = getVisibleShortcutCtas(
    availablePrimaryShortcutCtas,
  )

  return (
    <div className="items-center">
      <p className="m-0 mb-4 flex items-center text-s font-medium">
        <IconOpenInLaunchExternal className="size-6" />
        <FormattedMessage id="helpCenter.fragenBestellung.shortcuts.title" />
      </p>
      <div className="flex w-full flex-wrap gap-3">
        {visibleShortcutCtas.map(({ availability, Cta, key }) => (
          <Cta availability={availability} key={key} />
        ))}
      </div>
    </div>
  )
}
